<template>
  <div
    class="modal fade"
    id="formSummaryStockAttachmentModal"
    tabindex="-1"
    role="dialog"
    aria-hidden="true"
    ref="modalRoot"
  >
    <div class="modal-dialog modal-lg" role="document">
      <div class="modal-content" ref="modalContent">
        <div class="modal-header">
          <h5 class="modal-title">
            <i class="fas fa-paperclip"></i> แนบไฟล์เอกสาร
            <small v-if="documentNo" class="text-muted">
              — เลขที่เอกสาร {{ documentNo }}
            </small>
          </h5>
          <button
            type="button"
            class="close"
            data-dismiss="modal"
            aria-label="Close"
          >
            <span aria-hidden="true">&times;</span>
          </button>
        </div>

        <div class="modal-body">
          <!-- begin: ตัวนับจำนวนไฟล์ + ปุ่มดาวน์โหลดทั้งหมด -->
          <div class="row align-items-center mb-3">
            <div class="col-sm-6">
              <span class="kt-font-bold fss-attach__counter">
                {{ fileCount }} / {{ maxFiles }} ไฟล์
              </span>
            </div>
            <div class="col-sm-6 text-sm-right">
              <!--
                F08 (F2) — ดาวน์โหลดไฟล์แนบทั้งหมดเป็น ZIP โดยคงชื่อไฟล์เดิม
                FS-036 ข้อ 6 — spec ฉบับเซ็นวางปุ่มนี้ไว้ "มุมขวาบน" ข้างตัวนับจำนวนไฟล์
                  (เดิมอยู่ที่ footer ผู้ใช้ต้องเลื่อนผ่านรายการไฟล์ทั้งหมดก่อนจึงเจอ)
              -->
              <button
                type="button"
                class="fss-act-pill"
                data-test="fss-attach-download-all"
                v-if="fileCount > 0"
                :disabled="downloadingAll"
                @click="downloadAll"
              >
                <i class="fas" :class="downloadingAll ? 'fa-spinner fa-spin' : 'fa-arrow-circle-down'"></i>
                <span>{{ downloadingAll ? "กำลังรวมไฟล์..." : "ดาวน์โหลดทั้งหมด" }}</span>
              </button>
              <span v-if="isFull" class="kt-font-danger d-block mt-1">
                แนบครบแล้ว กรุณาลบไฟล์เดิมก่อนจึงจะแนบไฟล์ใหม่ได้
              </span>
            </div>
          </div>
          <!-- end: ตัวนับจำนวนไฟล์ + ปุ่มดาวน์โหลดทั้งหมด -->

          <!-- FS-051 — mockup ขั้นที่ 5 ของ spec เรียง รายการไฟล์ ไว้ก่อน ช่องลากไฟล์
               ของเดิมสลับกัน จึงย้ายตารางขึ้นมาก่อน -->
          <!-- begin: รายการไฟล์แนบ -->
          <div class="table-responsive mt-3">
            <table class="table table-sm table-bordered table-hover">
              <thead>
                <tr>
                  <th class="text-center" style="width: 8%;">ลำดับ</th>
                  <th class="text-left" style="width: 36%;">ชื่อไฟล์</th>
                  <th class="text-center" style="width: 12%;">ชนิด</th>
                  <th class="text-center" style="width: 12%;">ขนาด</th>
                  <th class="text-center" style="width: 17%;">วันเวลาที่อัพโหลด</th>
                  <th class="text-center" style="width: 15%;">คำสั่ง</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="fileCount === 0">
                  <td colspan="6" class="text-center text-muted">
                    ยังไม่มีไฟล์แนบในใบงานนี้
                  </td>
                </tr>
                <tr v-for="(file, index) in files" :key="file.id">
                  <td class="text-center">{{ index + 1 }}</td>
                  <td class="text-left fss-attach__filename">
                    <i :class="typeIcon(file)"></i> {{ file.fileName }}
                    <!--
                      FS-036 ข้อ 6 — spec ฉบับเซ็นมีหมายเหตุใต้ชื่อไฟล์ Excel/Word
                      เดิมแค่ "ซ่อนปุ่มดู" เฉย ๆ ผู้ใช้จึงนึกว่าไฟล์เสียหรือไม่มีสิทธิ์
                    -->
                    <div
                      v-if="isDownloadOnly(file)"
                      class="fss-attach__notice"
                      data-test="fss-attach-download-only-note"
                    >
                      Excel/Word ดูออนไลน์ไม่ได้ — ต้องดาวน์โหลดเปิดดูในเครื่อง
                    </div>
                  </td>
                  <td class="text-center">{{ typeLabel(file) }}</td>
                  <td class="text-center">{{ file.sizeText || "-" }}</td>
                  <td class="text-center">{{ formatDate(file.uploadedAt) }}</td>
                  <!--
                    QA 08/10/2026
                      เรื่องที่ 15 — ปุ่ม "ดู" เคยเป็นไอคอนดวงตา ไม่ตรงกับหน้ารายการเอกสาร
                                    ที่ใช้แว่นขยายสำหรับงานเดียวกัน → เปลี่ยนเป็นแว่นขยาย
                      เรื่องที่ 16 — วงกลม sm 28px: 🔵 ดู · 🟢 ดาวน์โหลด (วงกลมลูกศรลง) · 🔴 ลบ
                      เรื่องที่ 18 — ปุ่มดาวน์โหลดต้องได้ไฟล์ลงเครื่อง ไม่เปิดแท็บใหม่ (ดู downloadFile)

                    FS-079 (13/10/2026) — ผู้ใช้สั่งกลับมาใช้วงกลมของเรื่องที่ 16 ยกเลิก style /Jobs ของ FS-072
                  -->
                  <td class="text-center">
                    <div class="fss-act-group">
                      <a
                        v-if="file.canPreview"
                        href="#"
                        class="fss-act fss-act--sm fss-act--blue"
                        title="ดูรายละเอียด"
                        data-test="fss-attach-view"
                        @click.prevent="viewFile(file)"
                      >
                        <i class="fas fa-search"></i>
                      </a>
                      <a
                        href="#"
                        class="fss-act fss-act--sm fss-act--green"
                        :class="{ 'fss-act--disabled': downloadingId === file.id }"
                        title="ดาวน์โหลด"
                        data-test="fss-attach-download"
                        @click.prevent="downloadFile(file)"
                      >
                        <i
                          class="fas"
                          :class="downloadingId === file.id ? 'fa-spinner fa-spin' : 'fa-arrow-circle-down'"
                        ></i>
                      </a>
                      <a
                        href="#"
                        class="fss-act fss-act--sm fss-act--red"
                        title="ลบไฟล์"
                        data-test="fss-attach-delete"
                        @click.prevent="confirmDelete(file)"
                      >
                        <i class="fas fa-trash"></i>
                      </a>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <!-- end: รายการไฟล์แนบ -->

          <!-- begin: พื้นที่คลิกเลือกไฟล์ / ลากไฟล์มาวาง -->
          <div
            class="fss-attach__dropzone"
            :class="{
              'fss-attach__dropzone--over': isDragOver,
              'fss-attach__dropzone--disabled': isFull || uploading,
            }"
            @click="pickFile"
            @dragover.prevent="onDragOver"
            @dragenter.prevent="onDragOver"
            @dragleave.prevent="onDragLeave"
            @drop.prevent="onDrop"
          >
            <i class="fas fa-cloud-upload-alt fss-attach__dropzone-icon"></i>
            <div class="fss-attach__dropzone-title">
              คลิกเพื่อเลือกไฟล์ หรือลากไฟล์มาวางที่นี่
            </div>
            <div class="fss-attach__dropzone-hint">
              รองรับ PDF, Word (.doc/.docx), Excel (.xls/.xlsx) และรูปภาพ (.jpg/.png)
              — ขนาดไม่เกิน 10 MB ต่อไฟล์ สูงสุด {{ maxFiles }} ไฟล์ต่อใบงาน
            </div>
          </div>
          <input
            type="file"
            class="fss-attach__input"
            ref="fileInput"
            multiple
            :accept="acceptAttr"
            @change="onFileChange"
          />
          <!-- end: พื้นที่คลิกเลือกไฟล์ / ลากไฟล์มาวาง -->
        </div>

        <div class="modal-footer">
          <!-- FS-036 ข้อ 6 — ปุ่ม "ดาวน์โหลดทั้งหมด" ย้ายขึ้นไปมุมขวาบนของ popup แล้ว -->
          <button type="button" class="btn btn-secondary" data-dismiss="modal">
            <i class="la la-close"></i> ปิดหน้าต่าง
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
// Action Button Design System + Stepper ของโมดูลนี้ (QA เรื่องที่ 11 + 16)
import "./form-summary-stock-actions.css";
// FS-088 — tooltip ของปุ่มวงกลมเป็นแบบ Bootstrap (ผูกครั้งเดียวทั้งโมดูล เรียกซ้ำได้)
import { installFssTooltips } from "./form-summary-stock-tooltips";
installFssTooltips();
import moment from "moment";

require("sweetalert2");

// ─────────────────────────────────────────────────────────────────────────────
// CR-FormSummaryStock F08 — popup "แนบไฟล์เอกสารที่เซ็นแล้ว"
//
// เปิดจากปุ่มแนบไฟล์ในตารางของ FormSummaryStockList.vue ผ่าน $refs:
//   this.$parent.$refs.attachmentModal.open(rowData)
// ปิด popup แล้ว component emit "closed" ให้หน้ารายการ reload ตาราง (สีปุ่มแดง/เขียวจะได้ตรง)
//
// Template ที่ mirror: SBP.Web/App/Components/BudgetPlan/BudgetPlanList.vue (Import Modal)
//   — bootstrap 4 modal + FormData + axios + KTApp.block + Swal.fire
//
// SweetAlert2 = 8.13.3 (SBP.Web/package.json ^8.2.3) → ใช้ option ชื่อ `type` (ไม่ใช่ `icon` ของ v9+)
// และอ่านผลการยืนยันจาก `result.value`
//
// กฎทั้งหมดด้านล่างถูก "บังคับซ้ำที่ฝั่ง server" ใน UploadFormSummaryStockAttachmentCommandHandler
// ที่ตรวจในนี้เป็นแค่ด่านแรกเพื่อ UX (ไม่ต้องรอ round-trip)
// ─────────────────────────────────────────────────────────────────────────────

// ชนิดไฟล์ที่อนุญาต — ตรงกับ FormSummaryStockAttachmentRules.AllowedExtensions (backend)
const ALLOWED_EXTENSIONS = [
  ".pdf",
  ".doc",
  ".docx",
  ".xls",
  ".xlsx",
  ".jpg",
  ".jpeg",
  ".png",
];

// 10 MB ต่อไฟล์ — ตรงกับ FormSummaryStockAttachmentRules.MaxFileSizeBytes
const MAX_FILE_SIZE = 10 * 1024 * 1024;

// เพดานจำนวนไฟล์สำรอง กรณี API ไม่ได้ส่ง maxFiles กลับมา
const DEFAULT_MAX_FILES = 5;

// escape ก่อนยัดชื่อไฟล์ลง Swal html — ชื่อไฟล์มาจากผู้ใช้ ห้ามเชื่อ
function escapeHtml(value) {
  if (value === null || value === undefined) return "";
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

// ขนาดไฟล์อ่านง่าย ใช้เฉพาะข้อความเตือนฝั่ง UI (ตัวเลขที่แสดงในตารางมาจาก API)
function formatBytes(bytes) {
  if (!bytes || bytes <= 0) return "-";
  if (bytes >= 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(2) + " MB";
  if (bytes >= 1024) return (bytes / 1024).toFixed(2) + " KB";
  return bytes + " B";
}

function getExtension(fileName) {
  if (!fileName) return "";
  const dotIndex = String(fileName).lastIndexOf(".");
  if (dotIndex < 0) return "";
  return String(fileName)
    .substring(dotIndex)
    .toLowerCase();
}

export default {
  data: function() {
    return {
      // FormSummaryStock_Header.Id ของใบงานที่กำลังเปิดอยู่
      headerId: null,
      documentNo: "",
      // แถวไฟล์จาก API: { id, fileName, contentType, sizeText, downloadUrl, uploadedAt, canPreview }
      files: [],
      maxFiles: DEFAULT_MAX_FILES,
      loading: false,
      uploading: false,
      isDragOver: false,
      // F08 (F2) — กันกดซ้ำระหว่างรวมไฟล์
      downloadingAll: false,
      // QA เรื่องที่ 18 — id ของไฟล์ที่กำลังดาวน์โหลด (null = ไม่มี)
      downloadingId: null,
    };
  },
  computed: {
    fileCount: function() {
      return this.files ? this.files.length : 0;
    },
    isFull: function() {
      return this.fileCount >= this.maxFiles;
    },
    acceptAttr: function() {
      return ALLOWED_EXTENSIONS.join(",");
    },
  },
  methods: {
    // ── เปิด/ปิด popup ────────────────────────────────────────────────────
    // เรียกจากหน้ารายการ: this.$parent.$refs.attachmentModal.open(rowData)
    open: function(row) {
      if (!row || !row.id) {
        Swal.fire("เกิดข้อผิดพลาด", "ไม่พบใบงานที่ต้องการแนบไฟล์", "error");
        return;
      }

      this.headerId = row.id;
      this.documentNo = row.documentNo || "";
      this.files = [];
      this.isDragOver = false;
      this.uploading = false;
      this.downloadingAll = false;
      this.resetFileInput();

      // $refs safety — root element ไม่ได้อยู่ใน v-if จึงมีเสมอ แต่กันไว้เผื่อถูกเรียกก่อน mount เสร็จ
      if (this.$refs.modalRoot) {
        $(this.$refs.modalRoot).modal("show");
      }

      this.loadFiles();
    },

    // ── F08 (F2) : ดาวน์โหลดไฟล์แนบทั้งหมด ────────────────────────────────
    //
    //   endpoint ต้องการ Bearer token จึงยิงด้วย axios แล้วแปลงเป็น blob
    //   ใช้ <a href> ธรรมดาไม่ได้ เพราะการ navigate ของเบราว์เซอร์ไม่แนบ header ให้
    //   (บทเรียนเดียวกับปุ่มดาวน์โหลด ZIP ของ F13)
    //
    //   สูงสุด 5 ไฟล์ × 10 MB = 50 MB จึงรอในคำขอเดียวได้ ไม่ต้องมี progress
    downloadAll: function() {
      const self = this;

      if (!self.headerId || self.fileCount === 0 || self.downloadingAll) return;

      self.downloadingAll = true;
      KTApp.block("#formSummaryStockAttachmentModal .modal-content", {});

      axios({
        method: "post",
        url: "/Api/FormSummaryStock/Attachment/DownloadAll",
        responseType: "blob",
        data: { headerId: self.headerId },
        headers: { Authorization: "Bearer " + self.$root.user.access_token },
      })
        .then(function(response) {
          const contentType = response.headers["content-type"] || "";

          // server ปฏิเสธ (ไม่มีไฟล์ / ไม่มีสิทธิ์) จะคืน JSON ไม่ใช่ไฟล์
          if (contentType.indexOf("application/json") !== -1) {
            const reader = new FileReader();
            reader.onload = function() {
              let message = "ดาวน์โหลดไฟล์แนบไม่สำเร็จ";
              try {
                const parsed = JSON.parse(reader.result);
                if (parsed && parsed.message) message = parsed.message;
              } catch (e) {
                // ปล่อยข้อความเริ่มต้นไว้
              }
              Swal.fire("ดาวน์โหลดไม่ได้", message, "warning");
            };
            reader.readAsText(response.data);
            return;
          }

          // ชื่อไฟล์ถูก encode เป็น %20 ฝั่ง server แล้ว
          let filename = "";
          const disposition = response.headers["content-disposition"];
          if (disposition && disposition.indexOf("attachment") !== -1) {
            const marker = "filename=";
            const at = disposition.indexOf(marker);
            if (at !== -1) {
              filename = disposition.substring(at + marker.length);
              const semi = filename.indexOf(";");
              if (semi !== -1) filename = filename.substring(0, semi);
              filename = filename.trim().replace(/^["']|["']$/g, "");
            }
          }

          const link = document.createElement("a");
          link.href = window.URL.createObjectURL(response.data);
          link.download = decodeURIComponent(filename) || "เอกสารแนบ.zip";
          link.click();

          setTimeout(function() {
            window.URL.revokeObjectURL(link.href);
          }, 30000);
        })
        .catch(function() {
          Swal.fire(
            "เกิดข้อผิดพลาด",
            "ดาวน์โหลดไฟล์แนบไม่สำเร็จ กรุณาลองใหม่อีกครั้ง",
            "error"
          );
        })
        .then(function() {
          self.downloadingAll = false;
          KTApp.unblock("#formSummaryStockAttachmentModal .modal-content");
        });
    },

    // ── โหลดรายการไฟล์แนบของใบงาน ─────────────────────────────────────────
    loadFiles: function(showBlock) {
      let self = this;
      let block = showBlock !== false;

      if (!self.headerId) return Promise.resolve();

      self.loading = true;
      if (block) self.blockModal("กำลังโหลดข้อมูล กรุณารอสักครู่...");

      return axios
        .post(
          "/Api/FormSummaryStock/Attachments",
          { headerId: self.headerId },
          {
            headers: {
              Authorization: "Bearer " + self.$root.user.access_token,
            },
          }
        )
        .then(function(response) {
          let data = response.data || {};
          if (data.success) {
            self.files = data.files || [];
            if (data.maxFiles > 0) self.maxFiles = data.maxFiles;
          } else {
            self.files = [];
            Swal.fire(
              "เกิดข้อผิดพลาด",
              data.message || "ไม่สามารถโหลดรายการไฟล์แนบได้",
              "error"
            );
          }
        })
        .catch(function(error) {
          self.files = [];
          Swal.fire(
            "เกิดข้อผิดพลาด",
            self.resolveErrorMessage(error, "ไม่สามารถโหลดรายการไฟล์แนบได้"),
            "error"
          );
        })
        .then(function() {
          self.loading = false;
          if (block) self.unblockModal();
        });
    },

    // ── เลือกไฟล์ด้วยการคลิก ──────────────────────────────────────────────
    pickFile: function() {
      if (this.uploading) return;

      if (this.isFull) {
        Swal.fire(
          "แนบไฟล์ไม่ได้",
          "ใบงานนี้มีไฟล์แนบครบ " +
            this.maxFiles +
            " ไฟล์แล้ว กรุณาลบไฟล์เดิมออกก่อนจึงจะแนบไฟล์ใหม่ได้",
          "warning"
        );
        return;
      }

      if (this.$refs.fileInput) {
        this.$refs.fileInput.click();
      }
    },

    onFileChange: function(e) {
      let files = e && e.target ? e.target.files : null;
      this.uploadFiles(files);
    },

    // ── ลากไฟล์มาวาง ──────────────────────────────────────────────────────
    onDragOver: function() {
      if (this.uploading || this.isFull) return;
      this.isDragOver = true;
    },

    onDragLeave: function() {
      this.isDragOver = false;
    },

    onDrop: function(e) {
      this.isDragOver = false;

      if (this.uploading) return;

      if (this.isFull) {
        Swal.fire(
          "แนบไฟล์ไม่ได้",
          "ใบงานนี้มีไฟล์แนบครบ " +
            this.maxFiles +
            " ไฟล์แล้ว กรุณาลบไฟล์เดิมออกก่อนจึงจะแนบไฟล์ใหม่ได้",
          "warning"
        );
        return;
      }

      let files = e && e.dataTransfer ? e.dataTransfer.files : null;
      this.uploadFiles(files);
    },

    // ── อัพโหลด (ทีละไฟล์เรียงกัน แล้วสรุปผลครั้งเดียว) ───────────────────
    uploadFiles: function(fileList) {
      let self = this;

      if (!fileList || fileList.length === 0) return;
      if (self.uploading) return;

      let picked = Array.prototype.slice.call(fileList);
      let accepted = [];
      let rejected = [];

      picked.forEach(function(file) {
        let reason = self.validateFile(file, self.fileCount + accepted.length);
        if (reason) {
          rejected.push(escapeHtml(file.name) + " — " + reason);
        } else {
          accepted.push(file);
        }
      });

      if (accepted.length === 0) {
        self.resetFileInput();
        Swal.fire({
          title: "แนบไฟล์ไม่ได้",
          type: "warning",
          html: rejected.join("<br/>"),
        });
        return;
      }

      self.uploading = true;
      self.blockModal("กำลังอัพโหลดไฟล์ กรุณารอสักครู่...");

      let succeeded = 0;
      let failed = rejected.slice();
      let chain = Promise.resolve();

      accepted.forEach(function(file) {
        chain = chain
          .then(function() {
            return self.sendFile(file);
          })
          .then(function(result) {
            if (result.success) {
              succeeded++;
            } else {
              failed.push(escapeHtml(file.name) + " — " + escapeHtml(result.message));
            }
          });
      });

      chain
        .then(function() {
          return self.loadFiles(false);
        })
        .then(function() {
          self.uploading = false;
          self.unblockModal();
          self.resetFileInput();

          if (failed.length === 0) {
            Swal.fire("สำเร็จ", "แนบไฟล์เอกสารสำเร็จ " + succeeded + " ไฟล์", "success");
          } else if (succeeded > 0) {
            Swal.fire({
              title: "แนบไฟล์บางส่วนไม่สำเร็จ",
              type: "warning",
              html:
                "แนบสำเร็จ " +
                succeeded +
                " ไฟล์<br/>ไม่สำเร็จ " +
                failed.length +
                " ไฟล์<br/>" +
                failed.join("<br/>"),
            });
          } else {
            Swal.fire({
              title: "เกิดข้อผิดพลาด",
              type: "error",
              html: failed.join("<br/>"),
            });
          }
        });
    },

    // ส่ง 1 ไฟล์ — คืน { success, message } เสมอ (ไม่ reject) เพื่อให้ chain เดินต่อได้
    sendFile: function(file) {
      let self = this;

      let formData = new FormData();
      formData.append("headerId", self.headerId);
      formData.append("file", file);

      // ไม่กำหนด Content-Type เอง — axios/เบราว์เซอร์ต้องเป็นคนใส่ boundary ของ multipart ให้
      return axios
        .post("/Api/FormSummaryStock/Attachment/Upload", formData, {
          headers: {
            Authorization: "Bearer " + self.$root.user.access_token,
          },
        })
        .then(function(response) {
          let data = response.data || {};
          return {
            success: !!data.success,
            message: data.message || "แนบไฟล์ไม่สำเร็จ",
          };
        })
        .catch(function(error) {
          return {
            success: false,
            message: self.resolveErrorMessage(error, "แนบไฟล์ไม่สำเร็จ"),
          };
        });
    },

    // ตรวจก่อนส่ง — คืนข้อความเหตุผลเมื่อไฟล์ใช้ไม่ได้, คืน null เมื่อผ่าน
    validateFile: function(file, projectedCount) {
      if (!file) return "ไม่พบไฟล์";

      if (projectedCount >= this.maxFiles) {
        return "เกินจำนวนสูงสุด " + this.maxFiles + " ไฟล์ต่อใบงาน";
      }

      let ext = getExtension(file.name);
      if (ALLOWED_EXTENSIONS.indexOf(ext) < 0) {
        return "ชนิดไฟล์ไม่รองรับ (รองรับ PDF, Word, Excel และรูปภาพ .jpg/.png)";
      }

      if (!file.size || file.size <= 0) {
        return "ไฟล์ว่าง ไม่มีข้อมูล";
      }

      if (file.size > MAX_FILE_SIZE) {
        return "ขนาด " + formatBytes(file.size) + " เกิน 10 MB";
      }

      return null;
    },

    // ── ดู / ดาวน์โหลด ────────────────────────────────────────────────────
    // "ดู" = PDF และรูปภาพเท่านั้น (backend ส่ง canPreview มาให้) เปิด preview ในแถบใหม่
    viewFile: function(file) {
      if (!file || !file.downloadUrl) {
        Swal.fire("เกิดข้อผิดพลาด", "ไม่พบลิงก์ของไฟล์นี้ กรุณาลองใหม่อีกครั้ง", "error");
        return;
      }
      window.open(file.downloadUrl, "_blank", "noopener");
    },

    /**
     * QA 08/10/2026 เรื่องที่ 18 — ต้องดาวน์โหลดลงเครื่องจริง ไม่ใช่เปิดแท็บใหม่
     *
     * ของเดิมชี้ <a download> ไปที่ downloadUrl ซึ่งเป็น signed URL ของ GCS
     * attribute download ใช้ได้เฉพาะลิงก์ origin เดียวกัน เบราว์เซอร์จึงไม่สนใจแล้วเปิดแท็บแทน
     * กลายเป็นว่าปุ่มนี้ทำงานเหมือนปุ่ม "ดู" ทุกประการ
     *
     * ตอนนี้ดึงผ่าน endpoint ของแอปเอง (same-origin) แล้วบันทึกจาก blob
     */
    downloadFile: function(file) {
      const self = this;

      if (!file || !file.id) {
        Swal.fire("เกิดข้อผิดพลาด", "ไม่พบไฟล์ที่ต้องการดาวน์โหลด กรุณาลองใหม่อีกครั้ง", "error");
        return;
      }

      if (self.downloadingId) return;
      self.downloadingId = file.id;

      axios({
        method: "post",
        url: "/Api/FormSummaryStock/Attachment/Download",
        responseType: "blob",
        data: { headerId: self.headerId, attachmentId: file.id },
        headers: { Authorization: "Bearer " + self.$root.user.access_token },
      })
        .then(function(response) {
          const contentType = response.headers["content-type"] || "";

          // server ปฏิเสธ (ไม่มีสิทธิ์ / ไฟล์หาย) จะคืน JSON ไม่ใช่ไฟล์
          if (contentType.indexOf("application/json") !== -1) {
            const reader = new FileReader();
            reader.onload = function() {
              let message = "ดาวน์โหลดไฟล์ไม่สำเร็จ";
              try {
                const parsed = JSON.parse(reader.result);
                if (parsed && parsed.message) message = parsed.message;
              } catch (e) {
                // ปล่อยข้อความเริ่มต้นไว้
              }
              Swal.fire("ดาวน์โหลดไม่สำเร็จ", message, "error");
            };
            reader.readAsText(response.data);
            return;
          }

          self.saveBlob(response.data, file.fileName || "attachment");
        })
        .catch(function() {
          Swal.fire(
            "ดาวน์โหลดไม่สำเร็จ",
            "ไม่สามารถดาวน์โหลดไฟล์นี้ได้ กรุณาลองใหม่อีกครั้ง",
            "error"
          );
        })
        .then(function() {
          self.downloadingId = null;
        });
    },

    // บันทึก blob ลงเครื่อง
    saveBlob: function(blob, fileName) {
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // ปล่อย object URL ทีหลังเล็กน้อย — บางเบราว์เซอร์ยังอ่านอยู่ตอน click เพิ่งจบ
      window.setTimeout(function() {
        window.URL.revokeObjectURL(url);
      }, 1000);
    },

    // ── ลบไฟล์ (hard delete — ยืนยัน 1 ครั้งแล้วหายจริง) ──────────────────
    confirmDelete: function(file) {
      let self = this;

      if (!file || !file.id) {
        Swal.fire("เกิดข้อผิดพลาด", "ไม่พบไฟล์ที่ต้องการลบ", "error");
        return;
      }

      Swal.fire({
        title: "ยืนยันการลบไฟล์แนบ",
        type: "warning",
        html:
          "ต้องการลบไฟล์ <strong>" +
          escapeHtml(file.fileName) +
          "</strong> ใช่หรือไม่?<br/>" +
          "<small class='text-muted'>ไฟล์จะถูกลบออกจากระบบทันทีและกู้คืนไม่ได้ " +
          "(ต่างจากการลบใบงานที่ยังเก็บข้อมูลไว้ในฐานข้อมูล)</small>",
        showCancelButton: true,
        confirmButtonText: "ยืนยันลบ",
        cancelButtonText: "ยกเลิก",
        showLoaderOnConfirm: true,
      }).then(function(result) {
        if (result.value) {
          self.deleteFile(file);
        }
      });
    },

    deleteFile: function(file) {
      let self = this;

      self.blockModal("กำลังลบไฟล์ กรุณารอสักครู่...");

      axios
        .post(
          "/Api/FormSummaryStock/Attachment/Delete",
          { headerId: self.headerId, attachmentId: file.id },
          {
            headers: {
              Authorization: "Bearer " + self.$root.user.access_token,
            },
          }
        )
        .then(function(response) {
          let data = response.data || {};
          if (data.success) {
            return self.loadFiles(false).then(function() {
              Swal.fire("สำเร็จ", data.message || "ลบไฟล์แนบสำเร็จ", "success");
            });
          }
          Swal.fire("เกิดข้อผิดพลาด", data.message || "ลบไฟล์แนบไม่สำเร็จ", "error");
        })
        .catch(function(error) {
          Swal.fire(
            "เกิดข้อผิดพลาด",
            self.resolveErrorMessage(error, "ลบไฟล์แนบไม่สำเร็จ"),
            "error"
          );
        })
        .then(function() {
          self.unblockModal();
        });
    },

    // ── helper ────────────────────────────────────────────────────────────
    typeLabel: function(file) {
      let ext = this.normalizedType(file);
      if (ext === "pdf") return "PDF";
      if (ext === "doc" || ext === "docx") return "Word";
      if (ext === "xls" || ext === "xlsx") return "Excel";
      if (ext === "jpg" || ext === "jpeg" || ext === "png") return "รูปภาพ";
      return ext ? ext.toUpperCase() : "-";
    },

    // FS-036 ข้อ 6 — Excel/Word เปิดดูออนไลน์ไม่ได้ (ตรงกับ canPreview = false ฝั่ง backend)
    //   ตัดสินจาก "ชนิดไฟล์" ไม่ใช่ !canPreview เพราะ canPreview อาจไม่มีค่ามาจาก API
    //   แล้วหมายเหตุจะไปโผล่กับ PDF/รูปภาพซึ่งเปิดดูได้อยู่แล้ว
    isDownloadOnly: function(file) {
      let ext = this.normalizedType(file);
      return ext === "doc" || ext === "docx" || ext === "xls" || ext === "xlsx";
    },

    typeIcon: function(file) {
      let ext = this.normalizedType(file);
      if (ext === "pdf") return "fas fa-file-pdf text-danger";
      if (ext === "doc" || ext === "docx") return "fas fa-file-word text-primary";
      if (ext === "xls" || ext === "xlsx") return "fas fa-file-excel text-success";
      if (ext === "jpg" || ext === "jpeg" || ext === "png") return "fas fa-file-image text-info";
      return "fas fa-file";
    },

    // Attachment.ContentType ของระบบเก็บเป็น "นามสกุลไม่มีจุด" (Issue #33) แต่รองรับทั้ง 2 แบบไว้
    normalizedType: function(file) {
      if (!file) return "";
      let raw = file.contentType || getExtension(file.fileName);
      return String(raw)
        .toLowerCase()
        .replace(/^\./, "");
    },

    formatDate: function(value) {
      return value ? moment(value).format("DD/MM/YYYY HH:mm") : "-";
    },

    resetFileInput: function() {
      // $refs safety — input อยู่นอก v-if จึงมีเสมอ แต่กันไว้เผื่อ reset ถูกเรียกก่อน mount เสร็จ
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = "";
      }
    },

    // บังเฉพาะกล่อง modal ไม่บังทั้งหน้า เพราะ popup ยังเปิดค้างระหว่างทำงาน
    blockModal: function(message) {
      if (this.$refs.modalContent) {
        KTApp.block(this.$refs.modalContent, {
          overlayColor: "#000000",
          opacity: 0.05,
          message: message,
        });
      }
    },

    unblockModal: function() {
      if (this.$refs.modalContent) {
        KTApp.unblock(this.$refs.modalContent);
      }
    },

    resolveErrorMessage: function(error, fallback) {
      return error &&
        error.response &&
        error.response.data &&
        error.response.data.message
        ? error.response.data.message
        : fallback;
    },
  },
  mounted: function() {
    let self = this;

    if (self.$refs.modalRoot) {
      // ปิด popup แล้วให้หน้ารายการ reload ตาราง — สีปุ่มแนบไฟล์ (แดง/เขียว) จะได้ตรงกับจำนวนไฟล์จริง
      $(self.$refs.modalRoot).on("hidden.bs.modal", function() {
        self.files = [];
        self.isDragOver = false;
        self.uploading = false;
        self.resetFileInput();
        self.$emit("closed");
      });
    }
  },
  beforeDestroy: function() {
    if (this.$refs.modalRoot) {
      $(this.$refs.modalRoot).off("hidden.bs.modal");
    }
  },
};
</script>

<style lang="scss" scoped>
.fss-attach__counter {
  font-size: 1.1rem;
}

.fss-attach__dropzone {
  border: 2px dashed #d1d7e2;
  border-radius: 4px;
  padding: 24px 16px;
  text-align: center;
  cursor: pointer;
  background-color: #f7f8fa;
  transition: border-color 0.15s ease-in-out, background-color 0.15s ease-in-out;
}

.fss-attach__dropzone:hover {
  border-color: #5d78ff;
}

.fss-attach__dropzone--over {
  border-color: #5d78ff;
  background-color: #eef1ff;
}

.fss-attach__dropzone--disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.fss-attach__dropzone-icon {
  font-size: 2rem;
  color: #5d78ff;
  display: block;
  margin-bottom: 8px;
}

.fss-attach__dropzone-title {
  font-weight: 500;
}

.fss-attach__dropzone-hint {
  font-size: 0.9rem;
  color: #74788d;
  margin-top: 4px;
}

.fss-attach__input {
  display: none;
}

.fss-attach__filename {
  word-break: break-all;
}

/* FS-036 ข้อ 6 — หมายเหตุใต้ชื่อไฟล์ Excel/Word (โทนเดียวกับ .file-notice ใน mockup) */
.fss-attach__notice {
  margin-top: 2px;
  color: #fd397a;
  font-size: 0.8rem;
}
</style>



// WEBPACK FOOTER //
// App/Components/PrintSummary/FormSummaryStockAttachmentModal.vue